import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { TrendingUp, Trophy, Phone, Target, Loader2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  comercial: 'Comercial',
  chefe_equipa: 'Chefe de Equipa',
  admin: 'Admin',
  parceiro: 'Parceiro',
  backoffice: 'Backoffice',
  gestor: 'Gestor',
  super_admin: 'Super Admin',
};

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

// Tailwind text-color based on conversion rate
const rateColor = (rate) => {
  if (rate >= 30) return 'text-emerald-600';
  if (rate >= 15) return 'text-sky-600';
  if (rate >= 5) return 'text-amber-600';
  return 'text-slate-500';
};

const rateBg = (rate) => {
  if (rate >= 30) return 'bg-emerald-500';
  if (rate >= 15) return 'bg-sky-500';
  if (rate >= 5) return 'bg-amber-500';
  return 'bg-slate-400';
};

export const LeadsStatsPage = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState({ stats: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/leads/stats/conversion`, { params: { period } });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetch(); }, [fetch]);
  useRefreshListener(fetch);

  const totals = data.totals || {};
  const isAngariadores = data.audience === 'angariadores';
  const audienceSingular = isAngariadores ? 'Angariador' : 'Comercial';
  const audiencePlural = isAngariadores ? 'Angariadores' : 'Comerciais';

  return (
    <Layout>
      <div className="space-y-6" data-testid="leads-stats-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#2D3E6F]" />
              Performance {audiencePlural}
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Estatísticas de conversão por {audienceSingular.toLowerCase()}. Conversão = Leads Convertidas ÷ Total de Leads.
            </p>
          </div>
          <div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-44 h-10" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sempre</SelectItem>
                <SelectItem value="this_month">Este mês</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Aggregate cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="rounded-2xl border-slate-100 bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-slate-400" />
                <span className="text-3xl font-bold text-slate-700">{totals.comerciais_count ?? 0}</span>
              </div>
              <p className="text-sm font-semibold text-slate-700">{audiencePlural}</p>
              <p className="text-xs text-slate-400">No relatório</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 bg-gradient-to-br from-sky-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Phone className="w-5 h-5 text-sky-500" />
                <span className="text-3xl font-bold text-sky-700">{totals.total ?? 0}</span>
              </div>
              <p className="text-sm font-semibold text-sky-700">Total Leads</p>
              <p className="text-xs text-sky-400">Atribuídas no período</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5 text-amber-500" />
                <span className="text-3xl font-bold text-amber-700">{totals.contactadas ?? 0}</span>
              </div>
              <p className="text-sm font-semibold text-amber-700">Contactadas</p>
              <p className="text-xs text-amber-500">Pelo menos 1 contacto</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Trophy className="w-5 h-5 text-emerald-500" />
                <span className="text-3xl font-bold text-emerald-700">{totals.convertidas ?? 0}</span>
              </div>
              <p className="text-sm font-semibold text-emerald-700">Convertidas</p>
              <p className="text-xs text-emerald-500">{totals.conversion_rate ?? 0}% taxa global</p>
            </CardContent>
          </Card>
        </div>

        {/* Stats Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Ranking por Taxa de Conversão
            </CardTitle>
          </CardHeader>
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : data.stats.length === 0 ? (
            <CardContent className="text-center py-16">
              <Phone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400">Sem dados para o período selecionado</p>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="w-12 font-semibold text-center">#</TableHead>
                    <TableHead className="font-semibold">{audienceSingular}</TableHead>
                    <TableHead className="font-semibold text-center">Total</TableHead>
                    <TableHead className="font-semibold text-center">Contactadas</TableHead>
                    <TableHead className="font-semibold text-center">Reagendadas</TableHead>
                    <TableHead className="font-semibold text-center">Propostas</TableHead>
                    <TableHead className="font-semibold text-center">Convertidas</TableHead>
                    <TableHead className="font-semibold">Taxa Conversão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.stats.map((s, idx) => (
                    <TableRow key={s.user_id} className="hover:bg-slate-50/40" data-testid={`stat-row-${idx}`}>
                      <TableCell className="text-center">
                        {idx === 0 && s.convertidas > 0 ? (
                          <Trophy className="w-4 h-4 text-amber-500 mx-auto" title="Top performer" />
                        ) : (
                          <span className="text-xs text-slate-400 font-mono">{idx + 1}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-[#2D3E6F] text-white text-[10px] font-semibold">
                              {initials(s.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{s.name}</p>
                            <p className="text-xs text-slate-400">{ROLE_LABELS[s.role] || s.role}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm font-mono">{s.total}</TableCell>
                      <TableCell className="text-center text-sm font-mono text-amber-700">{s.contactadas}</TableCell>
                      <TableCell className="text-center text-sm font-mono text-sky-700">{s.reagendadas}</TableCell>
                      <TableCell className="text-center text-sm font-mono text-violet-700">{s.propostas}</TableCell>
                      <TableCell className="text-center text-sm font-mono font-bold text-emerald-700">{s.convertidas}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[180px]">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${rateBg(s.conversion_rate)}`}
                              style={{ width: `${Math.min(100, s.conversion_rate)}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold ${rateColor(s.conversion_rate)} min-w-[3rem] text-right`}>
                            {s.conversion_rate}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Legenda */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 px-2">
          <span className="font-semibold">Legenda:</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> ≥ 30% (excelente)</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-500" /> 15-29% (bom)</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> 5-14% (a melhorar)</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-400" /> &lt; 5% (atenção)</span>
        </div>
      </div>
    </Layout>
  );
};

export default LeadsStatsPage;
